import { LogtoProvider } from '@logto/react';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import { ErrorMessageProvider } from '@/components/common/ErrorMessageSystem';
import PageLoadingFallback from '@/components/common/PageLoadingFallback';
import { Toaster } from '@/components/ui/toaster';

import ProtectedRoute from './components/auth/ProtectedRoute';
import RequireAdminData from './components/auth/RequireAdminData';
import RequirePermission from './components/auth/RequirePermission';
import { BackendStatusBanner } from './components/BackendStatusBanner';
import { KeyboardShortcutsProvider } from './components/KeyboardShortcuts/KeyboardShortcutsProvider';
import Layout from './components/layout/Layout';
import NotificationDisplay from './components/notifications/NotificationDisplay';
import { AlarmProvider } from './components/providers/AlarmProvider';
import { WebSocketWrapper } from './components/WebSocketWrapper';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { VersionProvider } from './contexts/VersionContext';
import { logtoConfig } from './lib/logto';
// Page imports
import LoginPage from './pages/LoginPage';
import SystemOverviewPage from './pages/SystemOverviewPage';
import SystemSettings from './pages/SystemSettings';

const LogtoCallback = React.lazy(() => import('./pages/Auth/LogtoCallback'));
const MerchantsPage = React.lazy(() => import('./pages/admin/MerchantsPage'));

// Signage pages
const SignageDashboardPage = React.lazy(
  () => import('./pages/Signage/SignageDashboardPage')
);
const SignageAnnouncementsPage = React.lazy(
  () => import('./pages/Signage/AnnouncementsPage')
);
const SignageMenuConfigPage = React.lazy(
  () => import('./pages/Signage/MenuConfigPage')
);
const SignagePlaylistsPage = React.lazy(
  () => import('./pages/Signage/PlaylistsPage')
);
const SignageSettingsPage = React.lazy(
  () => import('./pages/Signage/SettingsPage')
);
const SignageDisplayDetailPage = React.lazy(
  () => import('./pages/Signage/DisplayDetailPage')
);

// Auth and Audit pages
const AuditLogsPage = React.lazy(() => import('./pages/Audit/AuditLogs'));

// Feature page imports (lazy load for better performance)
const OrgMembersPage = React.lazy(
  () => import('./pages/OrgMembers/OrgMembersPage')
);
const AcceptInvitationPage = React.lazy(
  () => import('./pages/OrgMembers/AcceptInvitationPage')
);
const KYCReview = React.lazy(() => import('./pages/KYCReviewPage'));
const CameraManagement = React.lazy(
  () => import('./pages/CameraManagementPage')
);
const CameraDetailPage = React.lazy(() => import('./pages/CameraDetailPage'));
const NVRDetailPage = React.lazy(() => import('./pages/NVRDetailPage'));
const HITLDataManagement = React.lazy(() => import('./pages/DataLabelingPage'));
const HITLQAReview = React.lazy(() => import('./pages/HITLQAReview'));
const HITLTrainingManagement = React.lazy(
  () => import('./components/hitl/HITLTrainingManagement')
);

// Agent Management pages
const AgentListPage = React.lazy(() => import('./pages/AgentListPage'));
const AgentDetailPage = React.lazy(() => import('./pages/AgentDetailPage'));
const AgentsPage = React.lazy(() => import('./pages/agents/AgentsPage'));
const PendingAgentsPage = React.lazy(() =>
  import('./pages/agents/PendingAgentsPage').then(module => ({
    default: module.PendingAgentsPage,
  }))
);

// Site Management pages
const SitesPage = React.lazy(() => import('./pages/sites/SitesPage'));
const SiteDetailPage = React.lazy(() => import('./pages/sites/SiteDetailPage'));

// Chatbot page
const ChatbotPage = React.lazy(() => import('./pages/Chatbot'));
const AnswerReviewPage = React.lazy(
  () => import('./pages/Chatbot/review/AnswerReviewPage')
);
const ChatbotAnalyticsPage = React.lazy(
  () => import('./pages/Chatbot/ChatbotAnalyticsPage')
);
const ChatbotTestingPage = React.lazy(
  () => import('./pages/Chatbot/ChatbotTestingPage')
);
const ChatbotAgentsPage = React.lazy(() => import('./pages/Chatbot/Agents'));
const ChatbotAgentNewPage = React.lazy(
  () => import('./pages/Chatbot/Agents/AgentEditorPage')
);
const ChatbotAgentTemplatesPage = React.lazy(
  () => import('./pages/Chatbot/Agents/templates')
);
const ChatbotSkillsPage = React.lazy(
  () => import('./pages/Chatbot/Skills/SkillsListPage')
);
const ChatbotSkillEditPage = React.lazy(
  () => import('./pages/Chatbot/Skills/SkillEditPage')
);
const ChatbotSkillCandidatesPage = React.lazy(
  () => import('./pages/Chatbot/Skills/Candidates/CandidateQueuePage')
);
const ChatbotSkillCandidateReviewPage = React.lazy(
  () => import('./pages/Chatbot/Skills/Candidates/CandidateReviewPage')
);
const LineConfigListPage = React.lazy(
  () => import('./pages/LineBot/LineConfigListPage')
);
const LineConfigFormPage = React.lazy(
  () => import('./pages/LineBot/LineConfigFormPage')
);
const BotDiscoveryPage = React.lazy(
  () => import('./pages/LineBot/BotDiscoveryPage')
);
const LineGroupsPage = React.lazy(
  () => import('./pages/LineBot/LineGroupsPage')
);
const LineGroupSendersPage = React.lazy(
  () => import('./pages/LineBot/LineGroupSendersPage')
);
const TelegramConfigListPage = React.lazy(
  () => import('./pages/TelegramBot/TelegramConfigListPage')
);
const TelegramConfigFormPage = React.lazy(
  () => import('./pages/TelegramBot/TelegramConfigFormPage')
);

// Call Center pages
const CallQualityReviewPage = React.lazy(
  () => import('./pages/Call/CallQualityReviewPage')
);
const CallDailyReportPage = React.lazy(
  () => import('./pages/Call/CallDailyReportPage')
);
const CallIndexPage = React.lazy(() => import('./pages/Call/index'));
const CallRecordingsPage = React.lazy(
  () => import('./pages/Call/CallRecordingsPage')
);
const RecordingDetailPage = React.lazy(
  () => import('./pages/Call/RecordingDetailPage')
);
const CallTestConsolePage = React.lazy(
  () => import('./pages/Call/CallTestConsolePage')
);
const UCMDeploymentsPage = React.lazy(
  () => import('./pages/Call/UCMDeploymentsPage')
);
const PipelineQueuePage = React.lazy(
  () => import('./pages/Call/PipelineQueuePage')
);
const CallSettingsPage = React.lazy(
  () => import('./pages/Call/CallSettingsPage')
);

// Product Management pages
const ProductListPage = React.lazy(
  () => import('./pages/product/ProductListPage')
);
const ProductFormPage = React.lazy(
  () => import('./pages/product/ProductFormPage')
);
const VectorSearchPage = React.lazy(
  () => import('./pages/product/VectorSearchPage')
);

// Video & Forensic pages
const ClipTestingPage = React.lazy(
  () => import('./pages/Video/ClipTestingPage')
);
const ClipArchivePage = React.lazy(
  () => import('./pages/Video/ClipArchivePage')
);
const ClipListPage = React.lazy(() => import('./pages/Video/ClipListPage'));
const ClipDetailPage = React.lazy(() => import('./pages/Video/ClipDetailPage'));
const FaceEventsPage = React.lazy(() => import('./pages/Video/FaceEventsPage'));
const AlarmEventsPage = React.lazy(
  () => import('./pages/Video/AlarmEventsPage')
);
const FaceConfigPage = React.lazy(() => import('./pages/Video/FaceConfigPage'));
const PersonManagementPage = React.lazy(
  () => import('./pages/Video/PersonManagementPage')
);
const AnalyticsPage = React.lazy(() => import('./pages/Video/AnalyticsPage'));
const WelcomeDisplayPage = React.lazy(
  () => import('./pages/Video/WelcomeDisplayPage')
);
const EvaluationHubPage = React.lazy(
  () => import('./pages/Video/EvaluationHub')
);

// ML Management pages (new stack)
const MLModelListPage = React.lazy(() => import('./pages/ML/ModelList'));
const MLModelUploadPage = React.lazy(() => import('./pages/ML/ModelUpload'));
const MLModelDetailPage = React.lazy(() => import('./pages/ML/ModelDetail'));
const MLModelDeployPage = React.lazy(() => import('./pages/ML/ModelDeploy'));
const ABTestCreatePage = React.lazy(() => import('./pages/ML/ABTestCreate'));
const MLAnalyticsPage = React.lazy(() => import('./pages/ML/Analytics'));

// Second-Brain Knowledge Management
const KnowledgeDocumentsPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeDocumentsPage')
);
const KnowledgeDocumentDetailPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeDocumentDetailPage')
);
const KnowledgeReviewQueuePage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeReviewQueuePage')
);
const KnowledgeDocumentReviewPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeDocumentReviewPage')
);
const KnowledgeEntitiesPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeEntitiesPage')
);
const KnowledgeEntityDetailPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgeEntityDetailPage')
);
const KnowledgePagesPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgePagesPage')
);
const KnowledgePageDetailPage = React.lazy(
  () => import('./pages/Knowledge/KnowledgePageDetailPage')
);

// Second-Brain Ingestion
const IngestionSourcesPage = React.lazy(
  () => import('./pages/Ingestion/IngestionSourcesPage')
);
const SourceDetailPage = React.lazy(
  () => import('./pages/Ingestion/SourceDetailPage')
);
const ImportJobsListPage = React.lazy(
  () => import('./pages/Ingestion/ImportJobsListPage')
);
const ImportJobDetailPage = React.lazy(
  () => import('./pages/Ingestion/ImportJobDetailPage')
);

// Second-Brain Market Intelligence
const CompetitorsPage = React.lazy(
  () => import('./pages/MarketIntel/CompetitorsPage')
);
const CompetitorDetailPage = React.lazy(
  () => import('./pages/MarketIntel/CompetitorDetailPage')
);
const MarketIntelPagesPage = React.lazy(
  () => import('./pages/MarketIntel/MarketIntelPagesPage')
);
const VendorProductsPage = React.lazy(
  () => import('./pages/MarketIntel/VendorProductsPage')
);
const PriceComparisonPage = React.lazy(
  () => import('./pages/MarketIntel/PriceComparisonPage')
);
const ObservationDetailPage = React.lazy(
  () => import('./pages/MarketIntel/ObservationDetailPage')
);
const DailyReportPage = React.lazy(
  () => import('./pages/MarketIntel/DailyReportPage')
);
const ABTestListPage = React.lazy(() => import('./pages/ML/ABTestList'));
const ABTestMonitorRoute = React.lazy(() => import('./pages/ML/ABTestMonitor'));

// OTA Updates pages
const OTACampaignCreatePage = React.lazy(
  () => import('./pages/ota/CampaignCreate')
);
const OTACampaignListPage = React.lazy(
  () => import('./pages/ota/CampaignList')
);
const OTACampaignMonitorPage = React.lazy(
  () => import('./pages/ota/CampaignMonitor')
);
const OTAPackageUploadPage = React.lazy(
  () => import('./pages/ota/PackageUpload')
);
const OTAAlertConfigurationPage = React.lazy(
  () => import('./pages/ota/AlertConfiguration')
);

// Fallback component for lazy loading
const Fallback = () => <PageLoadingFallback />;

// Single source of truth for query defaults AND the client instance lives in
// src/config/queryClient.ts (staleTime 5m, cacheTime 10m, refetchOnWindowFocus
// false, status-aware retry with exponential backoff). Importing the module
// singleton — rather than constructing a second client here — keeps that
// module's cache helpers (invalidateQueriesByPrefix, setQueryData, ...)
// operating on the same cache the Provider serves.
// Ingestion moved under /knowledge (DESIGN.md 0.2.0). Old links keep working.
function IngestionSourceRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={`/knowledge/ingestion/sources/${id}`} replace />;
}

function AgentNewRedirect() {
  return <Navigate to='/agents?create=true' replace />;
}

function AgentEditRedirect() {
  const { id } = useParams<{ id: string }>();
  const target = id
    ? `/agents?preview=${encodeURIComponent(id)}&edit=true`
    : '/agents';
  return <Navigate to={target} replace />;
}

function App() {
  const RouterWithFuture = Router as any;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterWithFuture
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <LogtoProvider config={logtoConfig}>
          <AuthProvider>
            <VersionProvider>
              <NotificationProvider>
                <ErrorMessageProvider>
                  <NotificationDisplay />
                  <WebSocketWrapper>
                    <AlarmProvider>
                      <KeyboardShortcutsProvider>
                        <ErrorBoundary>
                          <Routes>
                            {/* Public Routes */}
                            <Route
                              path='/video/welcome'
                              element={
                                <React.Suspense fallback={<Fallback />}>
                                  <WelcomeDisplayPage />
                                </React.Suspense>
                              }
                            />
                            <Route path='/login' element={<LoginPage />} />
                            <Route
                              path='/auth/callback'
                              element={
                                <React.Suspense fallback={<Fallback />}>
                                  <LogtoCallback />
                                </React.Suspense>
                              }
                            />

                            {/* Protected Routes with Layout */}
                            <Route
                              element={
                                <ProtectedRoute>
                                  <Layout />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={<Navigate to='/dashboard' replace />}
                              />
                              <Route
                                path='dashboard'
                                element={<SystemOverviewPage />}
                              />

                              {/* Organization members (Logto). The legacy
                                  local accounts list is a tab inside. */}
                              <Route
                                path='members'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <OrgMembersPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='users'
                                element={<Navigate to='/members' replace />}
                              />
                              <Route
                                path='invitations/accept'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AcceptInvitationPage />
                                  </React.Suspense>
                                }
                              />

                              {/* KYC Review */}
                              <Route
                                path='kyc-review'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KYCReview />
                                  </React.Suspense>
                                }
                              />

                              {/* HITL Routes */}
                              <Route
                                path='hitl/data'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <HITLDataManagement />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='hitl/qa'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <HITLQAReview />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='hitl/training'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <HITLTrainingManagement />
                                  </React.Suspense>
                                }
                              />

                              {/* Camera Management */}
                              <Route
                                path='cameras'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CameraManagement />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='cameras/nvr/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <NVRDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='cameras/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CameraDetailPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Agent Management */}
                              <Route
                                path='agents'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AgentsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='agents/pending'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <PendingAgentsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='agents-legacy'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AgentListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='agents/new'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AgentNewRedirect />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='agents/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AgentDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='agents/:id/edit'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AgentEditRedirect />
                                  </React.Suspense>
                                }
                              />

                              {/* Site Management */}
                              <Route
                                path='sites'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <SitesPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='sites/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <SiteDetailPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Chatbot */}
                              <Route
                                path='chatbot/review'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AnswerReviewPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='chatbot'
                                element={
                                  <RequirePermission
                                    permissions={[
                                      'chatbot:read',
                                      'chatbot:test',
                                    ]}
                                  >
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/analytics'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotAnalyticsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/testing'
                                element={
                                  <RequirePermission permissions='chatbot:test'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotTestingPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              {/* Chatbot Agents */}
                              <Route
                                path='chatbot/agents'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotAgentsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/agents/new'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotAgentNewPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/agents/:id/edit'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotAgentNewPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/agents/templates'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotAgentTemplatesPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              {/* Chatbot Skills */}
                              <Route
                                path='chatbot/skills'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotSkillsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/skills/candidates'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotSkillCandidatesPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/skills/candidates/:id'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotSkillCandidateReviewPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/skills/new'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotSkillEditPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='chatbot/skills/:id/edit'
                                element={
                                  <RequirePermission permissions='chatbot:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ChatbotSkillEditPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              <Route
                                path='line-bot'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <LineConfigListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='line-bot/discovery'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <BotDiscoveryPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='line-bot/groups'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <LineGroupsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='line-bot/groups/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <LineGroupSendersPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='line-bot/new'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <LineConfigFormPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='line-bot/:id/edit'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <LineConfigFormPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Telegram Bots */}
                              <Route
                                path='telegram-bot'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <TelegramConfigListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='telegram-bot/new'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <TelegramConfigFormPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='telegram-bot/:id/edit'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <TelegramConfigFormPage />
                                  </React.Suspense>
                                }
                              />
                              {/* Legacy alias: /editform → /edit (typo fix) */}
                              <Route
                                path='telegram-bot/:id/editform'
                                element={<Navigate to='../edit' replace />}
                              />

                              {/* Call Center */}
                              <Route
                                path='calls/review'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallQualityReviewPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/reports/daily'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallDailyReportPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallIndexPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/recordings'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallRecordingsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/recordings/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <RecordingDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/test'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallTestConsolePage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/ucm'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <UCMDeploymentsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/queue'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <PipelineQueuePage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='calls/settings'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CallSettingsPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Product Management */}
                              <Route
                                path='products'
                                element={
                                  <RequirePermission permissions='product:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ProductListPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='products/new'
                                element={
                                  <RequirePermission permissions='product:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ProductFormPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='products/:id/edit'
                                element={
                                  <RequirePermission permissions='product:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <ProductFormPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='products/search'
                                element={
                                  <RequirePermission permissions='product:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <VectorSearchPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              {/* OTA Updates */}
                              <Route
                                path='ota/campaigns'
                                element={
                                  <RequirePermission permissions='ota:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <OTACampaignListPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='ota/campaigns/new'
                                element={
                                  <RequirePermission permissions='ota:write'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <OTACampaignCreatePage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='ota/campaigns/:id'
                                element={
                                  <RequirePermission permissions='ota:read'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <OTACampaignMonitorPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='ota/packages'
                                element={
                                  <RequirePermission permissions='ota:write'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <OTAPackageUploadPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='ota/alerts'
                                element={
                                  <RequirePermission permissions='ota:write'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <OTAAlertConfigurationPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              {/* Second-Brain Knowledge */}
                              <Route
                                path='knowledge'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeDocumentsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/review'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeReviewQueuePage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/documents/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeDocumentDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/documents/:id/review'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeDocumentReviewPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/entities/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeEntityDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/entities'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgeEntitiesPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/pages'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgePagesPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/pages/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <KnowledgePageDetailPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Knowledge ingestion (moved from /ingestion) */}
                              <Route
                                path='knowledge/ingestion'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <IngestionSourcesPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/ingestion/sources/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <SourceDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/ingestion/jobs'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ImportJobsListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='knowledge/ingestion/jobs/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ImportJobDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ingestion'
                                element={
                                  <Navigate to='/knowledge/ingestion' replace />
                                }
                              />
                              <Route
                                path='ingestion/sources/:id'
                                element={<IngestionSourceRedirect />}
                              />

                              {/* Second-Brain Market Intelligence */}
                              <Route
                                path='market-intel'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CompetitorsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/competitors/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <CompetitorDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/pages'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MarketIntelPagesPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/vendor-products'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <VendorProductsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/observations/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ObservationDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/price-comparison'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <PriceComparisonPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='market-intel/reports/daily'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <DailyReportPage />
                                  </React.Suspense>
                                }
                              />

                              {/* Video & Forensic */}
                              <Route
                                path='video/clips'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ClipListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/clips/:clipId'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ClipDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/testing'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ClipTestingPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/evaluation-hub'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <EvaluationHubPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/archive'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ClipArchivePage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/events'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <FaceEventsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/alarm-events'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AlarmEventsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/analytics'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <AnalyticsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/face-config'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <FaceConfigPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='video/face-members'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <PersonManagementPage />
                                  </React.Suspense>
                                }
                              />

                              {/* System Settings */}
                              <Route
                                path='settings'
                                element={
                                  <RequireAdminData>
                                    <SystemSettings />
                                  </RequireAdminData>
                                }
                              />

                              {/* ML Management (new stack) */}
                              <Route
                                path='ml/analytics'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MLAnalyticsPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/models'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MLModelListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/models/upload'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MLModelUploadPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/models/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MLModelDetailPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/models/:id/deploy'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MLModelDeployPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/ab-tests'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ABTestListPage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/ab-tests/create'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ABTestCreatePage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path='ml/ab-tests/:id'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <ABTestMonitorRoute />
                                  </React.Suspense>
                                }
                              />

                              {/* Audit Logs */}
                              <Route
                                path='audit/logs'
                                element={
                                  <RequireAdminData>
                                    <React.Suspense fallback={<Fallback />}>
                                      <AuditLogsPage />
                                    </React.Suspense>
                                  </RequireAdminData>
                                }
                              />

                              {/* Signage */}
                              <Route
                                path='signage'
                                element={
                                  <RequirePermission permissions='signage:view'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignageDashboardPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='signage/announcements'
                                element={
                                  <RequirePermission permissions='signage:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignageAnnouncementsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='signage/menu'
                                element={
                                  <RequirePermission permissions='signage:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignageMenuConfigPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='signage/playlists'
                                element={
                                  <RequirePermission permissions='signage:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignagePlaylistsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='signage/settings'
                                element={
                                  <RequirePermission permissions='signage:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignageSettingsPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />
                              <Route
                                path='signage/displays/:id'
                                element={
                                  <RequirePermission permissions='signage:manage'>
                                    <React.Suspense fallback={<Fallback />}>
                                      <SignageDisplayDetailPage />
                                    </React.Suspense>
                                  </RequirePermission>
                                }
                              />

                              {/* Merchant Management */}
                              <Route
                                path='admin/merchants'
                                element={
                                  <React.Suspense fallback={<Fallback />}>
                                    <MerchantsPage />
                                  </React.Suspense>
                                }
                              />
                              {/* Catch-all route */}
                              <Route
                                path='*'
                                element={<Navigate to='/dashboard' replace />}
                              />
                            </Route>
                          </Routes>
                        </ErrorBoundary>
                      </KeyboardShortcutsProvider>
                    </AlarmProvider>
                  </WebSocketWrapper>
                </ErrorMessageProvider>
              </NotificationProvider>
            </VersionProvider>
          </AuthProvider>
        </LogtoProvider>
      </RouterWithFuture>
      <BackendStatusBanner />
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
